<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { ResourceService } from '../../api/resource/resourceService';
import { FileDownloadService } from '../../services/fileDownload/fileDownloadService';
import { UserRole } from '@common/contracts';
import { useUserStore } from '../../stores/userStore/userStore';
import { mapFileType } from './utils/mapFileType';
import prettyBytes from 'pretty-bytes';
import PreviewImage from '../PulsingImage/PreviewImage.vue';
import PreviewPictogram from '../PreviewPictogram/PreviewPictogram.vue';
import FileUploadDialog from '../FileUploadDialog/FileUploadDialog.vue';
import ShareDialog from '../ShareDialog/ShareDialog.vue';
import { useToast } from 'primevue/usetoast';
import { useRouter } from 'vue-router';

interface FileState {
  name: string;
  originalName: string;
  updatedAt: string;
  image: string;
  thumbnail: string;
  imageLoading: boolean;
  wish: string;
  contentSize: string;
  contentTypeMapped: string;
  contentType: string;
}

const router = useRouter();
const userStore = useUserStore();
const toast = useToast();

const loading = ref(true);
const files = ref<FileState[]>([]);
const selectedFiles = ref<FileState[]>([]);
const viewMode = ref<'grid' | 'list'>('list');
const pageNumber = ref(1);
const pageSize = ref(10);
const totalPages = ref(0);
const totalItems = ref(0);
const wishesVisible = ref(false);
const shareDialogVisible = ref(false);
const uploadDialogVisible = ref(false);
const downloadLoading = ref(false);
const deleteLoading = ref(false);
const errorMessage = ref('');
const successMessage = ref('');
const timeoutId = ref<number | null>(null);
const showSelectedFileInfo = ref(true);

const mutationButtonsVisibility = computed(
  () => userStore.getLoggedInUserRole && userStore.getLoggedInUserRole !== UserRole.guest,
);
const filteredFiles = computed(() => {
  return files.value;
});

const showError = (message: string) => {
  errorMessage.value = message;
  setTimeout(() => {
    errorMessage.value = '';
  }, 3000);
};

const showSuccess = (message: string) => {
  successMessage.value = message;
  setTimeout(() => {
    successMessage.value = '';
  }, 3000);
};

const mapFilesFromResponse = (data): void => {
  wishesVisible.value = false;
  files.value = data.map((file) => {
    let contentTypeMapped = mapFileType(file.contentType ? file.contentType.split('/')[1] : '');

    if (contentTypeMapped === 'nieznany') {
      const nameParts = file.originalName.split('.');
      const extension = nameParts[nameParts.length - 1];
      contentTypeMapped = mapFileType(extension);
    }

    if (file.tags?.wish !== '') {
      wishesVisible.value = true;
    }

    return {
      name: file.name,
      originalName: file.originalName,
      updatedAt: new Date(file.updatedAt).toLocaleDateString('pl-PL'),
      image: '',
      thumbnail: '',
      imageLoading: true,
      wish: file.tags?.wish ?? '',
      contentSize: prettyBytes(file.contentSize ?? 0),
      contentTypeMapped,
      contentType: file.contentType,
    };
  });
};

const fetchImages = async (files: FileState[]): Promise<void> => {
  const fetchPromises = files.map(async (file) => {
    try {
      if (file.contentTypeMapped !== 'zdjęcie' && file.contentTypeMapped !== 'wideo') {
        file.imageLoading = false;
        return;
      }

      const res = await ResourceService.downloadImage({
        name: file.name,
        width: 300,
        height: 300,
      });

      if (res) {
        const thumbnailUrl = URL.createObjectURL(res);
        file.thumbnail = thumbnailUrl;
        file.imageLoading = false;
      }
    } catch (error) {
      console.error(error);
      file.imageLoading = false;
      file.thumbnail = '';
      showError('Błąd podczas ładowania podglądu');
    }
  });

  await Promise.all(fetchPromises);
};

const fetchImagePreview = async (name: string): Promise<void> => {
  const file = files.value.find((file) => file.name === name);
  if (!file) return;

  try {
    const res = await ResourceService.downloadImage({
      name: file.name,
      width: 800,
      height: 800,
    });

    if (res) {
      const imageUrl = URL.createObjectURL(res);
      file.image = imageUrl;
    }
  } catch (error) {
    console.error(error);
    showError('Błąd podczas ładowania podglądu');
  }
};

const refetch = async (): Promise<void> => {
  loading.value = true;
  files.value = [];
  try {
    const controller = new AbortController();
    const res = await ResourceService.findResources({
      page: pageNumber.value,
      pageSize: pageSize.value,
      abortSignal: controller.signal,
    });

    if (res.data.length === 0 && pageNumber.value !== 1) {
      pageNumber.value--;
      await refetch();
      return;
    }

    mapFilesFromResponse(res.data);
    totalPages.value = res.metadata.totalPages || 1;
    totalItems.value = res.metadata.totalItems || 0;
    await fetchImages(files.value);
  } catch (error) {
    console.error(error);
    files.value = [];
    totalPages.value = 1;
    totalItems.value = 0;
    showError('Błąd podczas pobierania plików');
  } finally {
    loading.value = false;
  }
};

const onPageChange = async (page: number): Promise<void> => {
  if (pageNumber.value === page) return;
  pageNumber.value = page;
  await refetch();
};

const onPageSizeChange = async (size: number): Promise<void> => {
  pageSize.value = size;
  totalPages.value = Math.max(1, Math.ceil(totalItems.value / size));
  if (pageNumber.value > totalPages.value) {
    pageNumber.value = totalPages.value;
  }
  await refetch();
};

const toggleSelect = (file: FileState): void => {
  const index = selectedFiles.value.findIndex((f) => f.name === file.name);
  if (index === -1) {
    selectedFiles.value.push(file);
  } else {
    selectedFiles.value.splice(index, 1);
  }
};

const isSelected = (file: FileState): boolean => {
  return selectedFiles.value.some((f) => f.name === file.name);
};

const downloadFile = async (file: FileState): Promise<void> => {
  try {
    const res = await ResourceService.downloadResources({
      names: [file.name],
    });
    FileDownloadService.downloadFilesWithUrl({
      url: res,
      fileName: file.originalName || file.name,
    });
    showSuccess('Pobieranie rozpoczęte');
  } catch (error) {
    console.error('Download error:', error);
    showError('Błąd podczas pobierania pliku');
  }
};

const deleteFile = async (file: FileState): Promise<void> => {
  if (!confirm('Czy na pewno chcesz usunąć ten plik?')) return;

  try {
    await ResourceService.deleteResource({ name: file.name });
    await refetch();
    showSuccess('Plik został usunięty');
  } catch (error) {
    console.error('Delete error:', error);
    showError('Błąd podczas usuwania pliku');
  }
};

const downloadSelected = async (): Promise<void> => {
  if (selectedFiles.value.length === 0) return;

  downloadLoading.value = true;
  try {
    toast.add({
      severity: 'info',
      summary: 'Informacja',
      detail: 'Przygotowywanie plików do pobrania...',
      life: 0,
    });

    timeoutId.value = window.setTimeout(() => {
      toast.add({
        severity: 'info',
        summary: 'Informacja',
        detail: 'Serwer potrzebuje więcej czasu na przygotowanie plików, prosimy o cierpliwość...',
        life: 0,
      });
    }, 20000);

    const res = await ResourceService.downloadResources({
      names: selectedFiles.value.map((f) => f.name),
    });

    if (timeoutId.value) {
      clearTimeout(timeoutId.value);
      timeoutId.value = null;
    }

    toast.removeAllGroups();

    FileDownloadService.downloadFilesWithUrl({
      url: res,
      fileName: 'pliki.zip',
    });
    selectedFiles.value = [];
    showSuccess('Pobieranie wybranych plików rozpoczęte');
  } catch (error) {
    if (timeoutId.value) {
      clearTimeout(timeoutId.value);
      timeoutId.value = null;
    }
    toast.removeAllGroups();
    console.error('Download error:', error);
    showError('Błąd podczas pobierania plików');
  } finally {
    downloadLoading.value = false;
  }
};

const deleteSelected = async (): Promise<void> => {
  if (selectedFiles.value.length === 0) return;
  if (!confirm('Czy na pewno chcesz usunąć wybrane pliki?')) return;

  deleteLoading.value = true;
  try {
    await Promise.all(selectedFiles.value.map((file) => ResourceService.deleteResource({ name: file.name })));
    selectedFiles.value = [];
    await refetch();
    showSuccess('Wybrane pliki zostały usunięte');
  } catch (error) {
    console.error('Delete error:', error);
    showError('Błąd podczas usuwania plików');
  } finally {
    deleteLoading.value = false;
  }
};

const downloadAll = async (): Promise<void> => {
  if (files.value.length === 0) return;

  downloadLoading.value = true;
  try {
    toast.add({
      severity: 'info',
      summary: 'Informacja',
      detail: 'Przygotowywanie plików do pobrania...',
      life: 0,
    });

    timeoutId.value = window.setTimeout(() => {
      toast.add({
        severity: 'info',
        summary: 'Informacja',
        detail: 'Serwer potrzebuje więcej czasu na przygotowanie plików, prosimy o cierpliwość...',
        life: 0,
      });
    }, 20000);

    const res = await ResourceService.downloadResources();

    if (timeoutId.value) {
      clearTimeout(timeoutId.value);
      timeoutId.value = null;
    }

    toast.removeAllGroups();

    FileDownloadService.downloadFilesWithUrl({
      url: res,
      fileName: 'wszystkie_pliki.zip',
    });
    showSuccess('Pobieranie wszystkich plików rozpoczęte');
  } catch (error) {
    if (timeoutId.value) {
      clearTimeout(timeoutId.value);
      timeoutId.value = null;
    }
    toast.removeAllGroups();
    console.error('Download error:', error);
    showError('Błąd podczas pobierania wszystkich plików');
  } finally {
    downloadLoading.value = false;
  }
};

const deleteAll = async (): Promise<void> => {
  if (!confirm('Czy na pewno chcesz usunąć wszystkie pliki?')) return;

  deleteLoading.value = true;
  try {
    await Promise.all(files.value.map((file) => ResourceService.deleteResource({ name: file.name })));
    await refetch();
    showSuccess('Wszystkie pliki zostały usunięte');
  } catch (error) {
    console.error('Delete error:', error);
    showError('Błąd podczas usuwania wszystkich plików');
  } finally {
    deleteLoading.value = false;
  }
};

const goBack = () => {
  router.back();
};

onMounted(async () => {
  await refetch();
});

watch(files, async () => {
  await fetchImages(files.value);
});

onUnmounted(() => {
  files.value.forEach((file) => {
    if (file.thumbnail) URL.revokeObjectURL(file.thumbnail);
    if (file.image) URL.revokeObjectURL(file.image);
  });
});
</script>

<template>
  <div class="file-manager">
    <!-- Main Content -->
    <div class="file-manager__content">
      <!-- Top Bar -->
      <div class="file-manager__top-bar">
        <div class="file-manager__view-controls">
          <!-- Back Button -->
          <button
            class="file-manager__back-button"
            @click="goBack"
          >
            <i class="pi pi-arrow-left"></i>
            <span>Powrót</span>
          </button>
          <button
            class="file-manager__view-button"
            :class="{ 'file-manager__view-button--active': viewMode === 'grid' }"
            @click="viewMode = 'grid'"
          >
            <i class="file-manager__icon file-manager__icon--grid"></i>
          </button>
          <button
            class="file-manager__view-button"
            :class="{ 'file-manager__view-button--active': viewMode === 'list' }"
            @click="viewMode = 'list'"
          >
            <i class="file-manager__icon file-manager__icon--list"></i>
          </button>
        </div>

        <div class="file-manager__actions">
          <button
            v-if="selectedFiles.length > 0"
            class="file-manager__action-button"
            :disabled="downloadLoading"
            @click="downloadSelected"
          >
            <i class="file-manager__icon file-manager__icon--download"></i>
            Pobierz wybrane
          </button>
          <button
            v-if="selectedFiles.length > 0 && mutationButtonsVisibility"
            class="file-manager__action-button file-manager__action-button--danger"
            :disabled="deleteLoading"
            @click="deleteSelected"
          >
            <i class="file-manager__icon file-manager__icon--delete"></i>
            Usuń wybrane
          </button>
          <button
            v-if="mutationButtonsVisibility"
            class="file-manager__action-button file-manager__action-button--secondary"
            @click="uploadDialogVisible = true"
          >
            <i class="file-manager__icon file-manager__icon--upload"></i>
            Prześlij
          </button>
          <button
            v-if="mutationButtonsVisibility"
            class="file-manager__action-button file-manager__action-button--secondary"
            @click="shareDialogVisible = true"
          >
            <i class="file-manager__icon file-manager__icon--share"></i>
            Udostępnij
          </button>
          <button
            class="file-manager__action-button"
            @click="downloadAll"
          >
            <i class="file-manager__icon file-manager__icon--download"></i>
            Pobierz wszystko
          </button>
          <button
            v-if="mutationButtonsVisibility"
            class="file-manager__action-button file-manager__action-button--danger"
            @click="deleteAll"
          >
            <i class="file-manager__icon file-manager__icon--delete"></i>
            Usuń wszystko
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div
        v-if="loading"
        class="file-manager__loading"
      >
        <div class="file-manager__spinner"></div>
        <p>Ładowanie...</p>
      </div>

      <!-- Grid View -->
      <transition
        name="fade"
        mode="out-in"
      >
        <div
          v-if="viewMode === 'grid'"
          class="file-manager__grid"
        >
          <div
            v-for="file in filteredFiles"
            :key="file.name"
            class="file-manager__grid-item"
            :class="{ 'file-manager__grid-item--selected': isSelected(file) }"
            @click="toggleSelect(file)"
          >
            <div class="file-manager__preview">
              <div
                v-if="file.imageLoading"
                class="file-manager__preview-loading"
              >
                <PreviewPictogram
                  v-if="file.contentTypeMapped === 'wideo'"
                  :content-type="file.contentTypeMapped"
                />
                <div
                  v-else
                  class="file-manager__spinner"
                ></div>
              </div>
              <PreviewImage
                v-else-if="file.thumbnail"
                :content-type="file.contentTypeMapped"
                :image-loading="file.imageLoading"
                :thumbnail="file.thumbnail"
                :image="file.image"
                @loading-started="fetchImagePreview(file.name)"
              />
              <div
                v-else
                class="file-manager__preview-placeholder"
              >
                <PreviewPictogram :content-type="file.contentTypeMapped" />
              </div>
            </div>
            <div class="file-manager__item-info">
              <span class="file-manager__filename">{{ file.originalName || file.name }}</span>
              <div class="file-manager__item-meta">
                <span class="file-manager__date">{{ file.updatedAt }}</span>
                <span class="file-manager__size">{{ file.contentSize }}</span>
              </div>
              <div
                v-if="file.wish"
                class="file-manager__wish"
              >
                <i class="file-manager__icon file-manager__icon--heart"></i>
                <span class="file-manager__wish-text">{{ file.wish }}</span>
              </div>
            </div>
            <div class="file-manager__item-actions">
              <button
                class="file-manager__action-button file-manager__action-button--small"
                @click.stop="downloadFile(file)"
              >
                <i class="file-manager__icon file-manager__icon--download"></i>
              </button>
              <button
                v-if="mutationButtonsVisibility"
                class="file-manager__action-button file-manager__action-button--small file-manager__action-button--danger"
                @click.stop="deleteFile(file)"
              >
                <i class="file-manager__icon file-manager__icon--delete"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- List View -->
        <div
          v-else
          class="file-manager__list"
        >
          <table class="file-manager__table">
            <thead>
              <tr>
                <th class="file-manager__table-header">
                  <input
                    type="checkbox"
                    :checked="selectedFiles.length === filteredFiles.length"
                    @change="selectedFiles = selectedFiles.length === filteredFiles.length ? [] : [...filteredFiles]"
                  />
                </th>
                <th class="file-manager__table-header">Nazwa pliku</th>
                <th class="file-manager__table-header">Podgląd</th>
                <th
                  v-if="wishesVisible"
                  class="file-manager__table-header"
                >
                  Życzenia
                </th>
                <th class="file-manager__table-header">Rozmiar</th>
                <th class="file-manager__table-header">Data</th>
                <th class="file-manager__table-header">Typ</th>
                <th class="file-manager__table-header">Akcje</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="file in filteredFiles"
                :key="file.name"
                class="file-manager__table-row"
                :class="{ 'file-manager__table-row--selected': isSelected(file) }"
              >
                <td class="file-manager__table-cell">
                  <input
                    type="checkbox"
                    :checked="isSelected(file)"
                    @change="toggleSelect(file)"
                  />
                </td>
                <td class="file-manager__table-cell">{{ file.originalName || file.name }}</td>
                <td class="file-manager__table-cell">
                  <div class="file-manager__preview">
                    <div
                      v-if="file.imageLoading"
                      class="file-manager__preview-loading"
                    >
                      <PreviewPictogram
                        v-if="file.contentTypeMapped === 'wideo'"
                        :content-type="file.contentTypeMapped"
                      />
                      <div
                        v-else
                        class="file-manager__spinner"
                      ></div>
                    </div>
                    <PreviewImage
                      v-else-if="file.thumbnail"
                      :content-type="file.contentTypeMapped"
                      :image-loading="file.imageLoading"
                      :thumbnail="file.thumbnail"
                      :image="file.image"
                      @loading-started="fetchImagePreview(file.name)"
                    />
                    <div
                      v-else
                      class="file-manager__preview-placeholder"
                    >
                      <PreviewPictogram :content-type="file.contentTypeMapped" />
                    </div>
                  </div>
                </td>
                <td
                  v-if="wishesVisible"
                  class="file-manager__table-cell"
                >
                  <div
                    v-if="file.wish"
                    class="file-manager__wish"
                  >
                    <i class="file-manager__icon file-manager__icon--heart"></i>
                    <span class="file-manager__wish-text">{{ file.wish }}</span>
                  </div>
                </td>
                <td class="file-manager__table-cell">{{ file.contentSize }}</td>
                <td class="file-manager__table-cell">{{ file.updatedAt }}</td>
                <td class="file-manager__table-cell">{{ file.contentTypeMapped }}</td>
                <td class="file-manager__table-cell">
                  <div class="file-manager__item-actions">
                    <button
                      class="file-manager__action-button file-manager__action-button--small"
                      @click="downloadFile(file)"
                    >
                      <i class="file-manager__icon file-manager__icon--download"></i>
                    </button>
                    <button
                      v-if="mutationButtonsVisibility"
                      class="file-manager__action-button file-manager__action-button--small file-manager__action-button--danger"
                      @click="deleteFile(file)"
                    >
                      <i class="file-manager__icon file-manager__icon--delete"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </transition>

      <!-- Empty State -->
      <div
        v-if="filteredFiles.length === 0"
        class="file-manager__empty"
      >
        <i class="file-manager__icon file-manager__icon--empty"></i>
        <p class="file-manager__empty-text">Brak plików</p>
        <p class="file-manager__empty-subtext">Dodaj nowe pliki, aby rozpocząć</p>
      </div>

      <!-- Pagination -->
      <div class="file-manager__pagination">
        <div class="file-manager__pagination-controls">
          <button
            class="file-manager__pagination-button"
            :disabled="pageNumber <= 1"
            @click="onPageChange(pageNumber - 1)"
          >
            <i class="file-manager__icon file-manager__icon--prev"></i>
          </button>
          <span class="file-manager__pagination-info"> Strona {{ pageNumber }} z {{ totalPages }} </span>
          <button
            class="file-manager__pagination-button"
            :disabled="pageNumber >= totalPages"
            @click="onPageChange(pageNumber + 1)"
          >
            <i class="file-manager__icon file-manager__icon--next"></i>
          </button>
        </div>
        <div class="file-manager__page-size">
          <select
            v-model="pageSize"
            class="file-manager__page-size-select"
            @change="onPageSizeChange(Number(($event.target as HTMLSelectElement).value))"
          >
            <option value="10">10 na stronę</option>
            <option value="25">25 na stronę</option>
            <option value="50">50 na stronę</option>
          </select>
        </div>
      </div>

      <!-- Selected Files Panel -->
      <div
        v-if="showSelectedFileInfo && selectedFiles.length > 0"
        class="file-manager__selected-info"
      >
        <div class="file-manager__selected-info-content">
          <div class="file-manager__selected-info-header">
            <h3 class="file-manager__selected-info-title">Wybrane pliki ({{ selectedFiles.length }})</h3>
            <button
              class="file-manager__selected-info-close"
              @click="showSelectedFileInfo = false"
            >
              <i class="file-manager__icon file-manager__icon--close"></i>
            </button>
          </div>

          <div class="file-manager__selected-files-list">
            <div
              v-for="file in selectedFiles"
              :key="file.name"
              class="file-manager__selected-file-item"
            >
              <div class="file-manager__selected-file-preview">
                <div
                  v-if="file.imageLoading"
                  class="file-manager__preview-loading"
                >
                  <PreviewPictogram
                    v-if="file.contentTypeMapped === 'wideo'"
                    :content-type="file.contentTypeMapped"
                  />
                  <div
                    v-else
                    class="file-manager__spinner"
                  ></div>
                </div>
                <PreviewImage
                  v-else-if="file.thumbnail"
                  :content-type="file.contentTypeMapped"
                  :image-loading="file.imageLoading"
                  :thumbnail="file.thumbnail"
                  :image="file.image"
                  @loading-started="fetchImagePreview(file.name)"
                />
                <div
                  v-else
                  class="file-manager__preview-placeholder"
                >
                  <PreviewPictogram :content-type="file.contentTypeMapped" />
                </div>
              </div>
              <div class="file-manager__selected-file-info">
                <span class="file-manager__selected-file-name">{{ file.originalName || file.name }}</span>
                <span class="file-manager__selected-file-size">{{ file.contentSize }}</span>
              </div>
              <button
                class="file-manager__selected-file-remove"
                @click="toggleSelect(file)"
              >
                <i class="file-manager__icon file-manager__icon--close"></i>
              </button>
            </div>
          </div>

          <div class="file-manager__selected-info-actions">
            <button
              class="file-manager__action-button"
              :disabled="downloadLoading"
              @click="downloadSelected"
            >
              <i class="file-manager__icon file-manager__icon--download"></i>
              Pobierz wybrane
            </button>
            <button
              v-if="mutationButtonsVisibility"
              class="file-manager__action-button file-manager__action-button--danger"
              :disabled="deleteLoading"
              @click="deleteSelected"
            >
              <i class="file-manager__icon file-manager__icon--delete"></i>
              Usuń wybrane
            </button>
          </div>
        </div>
      </div>

      <!-- Toggle Button -->
      <button
        v-if="!showSelectedFileInfo && selectedFiles.length > 0"
        class="file-manager__toggle-button"
        @click="showSelectedFileInfo = true"
      >
        <i class="file-manager__icon file-manager__icon--list"></i>
        <span class="file-manager__toggle-button-text">Wybrane pliki ({{ selectedFiles.length }})</span>
      </button>
    </div>

    <!-- File Upload Dialog -->
    <FileUploadDialog
      :upload-dialog-visible="uploadDialogVisible"
      @close="uploadDialogVisible = false"
      @data-uploaded="refetch"
    />

    <!-- Share Dialog -->
    <ShareDialog
      :dialog-visible="shareDialogVisible"
      @hide="shareDialogVisible = false"
      @link-copied="showSuccess('Link został skopiowany do schowka')"
      @emails-sent="showSuccess('Linki zostały wysłane')"
      @error="showError"
    />

    <!-- Toast Messages -->
    <div
      v-if="errorMessage"
      class="file-manager__message file-manager__message--error"
    >
      {{ errorMessage }}
    </div>

    <div
      v-if="successMessage"
      class="file-manager__message file-manager__message--success"
    >
      {{ successMessage }}
    </div>
  </div>
  <Toast position="bottom-right" />
</template>

<style lang="scss" scoped>
/* stylelint-disable */
.file-manager {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: $white-100;
  border-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba($black-100, 0.1);
  position: relative;

  &__message {
    position: fixed;
    bottom: 1rem;
    left: 50%;
    transform: translateX(-50%);
    padding: 1rem 2rem;
    border-radius: 0.5rem;
    font-size: 1.5rem;
    animation: slide-up 0.3s ease-out;
    box-shadow: 0 4px 8px rgba($black-100, 0.1);
    backdrop-filter: blur(4px);
    z-index: 1000;
    pointer-events: none;

    &--error {
      background-color: rgba($brown-200, 0.9);
      color: white;
      border: 1px solid rgba($brown-200, 0.3);
    }

    &--success {
      background-color: rgba(34, 197, 94, 0.9);
      color: white;
      border: 1px solid rgba(22, 163, 74, 0.3);
    }
  }

  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    color: $brown-300;
  }

  &__spinner {
    width: 3rem;
    height: 3rem;
    border: 3px solid $brown-300;
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  &__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 1rem;
    gap: 1rem;
  }

  &__top-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    padding: 1rem;
    background-color: $white-200;
    border-radius: 0.5rem;
    margin-bottom: 0;

    @media (max-width: $media-max-phone) {
      flex-direction: column;
      gap: 0.5rem;
    }

    @media (min-width: $media-min-tablet) {
      flex-wrap: nowrap;
      justify-content: space-between;
    }
  }

  &__view-controls {
    display: flex;
    gap: 0.5rem;
    padding: 0.5rem;
    background-color: $white-200;
    border-radius: 0.5rem;
  }

  &__view-button {
    width: 3rem;
    height: 3rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid $brown-300;
    color: $brown-100;
    padding: 0;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background-color: $brown-400;
      color: $white;
    }

    &--active {
      background-color: $brown-300;
      color: $white;
    }
  }

  &__actions {
    display: flex;
    gap: 1rem;
    padding: 1rem;
    background-color: $white-200;
    border-radius: 0.5rem;
    box-shadow: 0 2px 4px rgba($black-100, 0.05);

    @media (max-width: $media-max-phone) {
      flex-wrap: wrap;
      justify-content: center;
      gap: 0.5rem;
    }
  }

  &__action-button {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1.5rem;
    border: 1px solid $brown-300;
    border-radius: 0.5rem;
    background-color: $brown-200;
    color: $white;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 1.68rem;
    box-shadow: 0 2px 4px rgba($black-100, 0.1);

    @media (max-width: $media-max-phone) {
      padding: 0.5rem 1rem;
      font-size: 1.5rem;
      flex: 1 1 calc(50% - 0.5rem);
      justify-content: center;
    }

    &:hover {
      background-color: $brown-100;
      color: $white;
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba($black-100, 0.15);
    }

    &:active {
      transform: translateY(0);
      box-shadow: 0 2px 4px rgba($black-100, 0.1);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      background-color: $brown-300;
      color: $white;
      transform: none;
      box-shadow: none;
    }

    &--small {
      padding: 0.75rem;
      font-size: 1.5rem;
    }

    &--danger {
      background-color: #dc3545;
      color: $white;
      border-color: #dc3545;

      &:hover {
        background-color: #c82333;
        border-color: #c82333;
      }

      &:disabled {
        background-color: #e4606d;
        border-color: #e4606d;
        color: $white;
      }
    }

    &--secondary {
      background-color: $white;
      color: $brown-100;
      border-color: $brown-300;

      &:hover {
        background-color: $brown-400;
        color: $white;
      }

      &:disabled {
        background-color: $white;
        color: $brown-300;
      }
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1.5rem;
    padding: 1rem;

    @media (max-width: $media-max-phone) {
      grid-template-columns: 1fr;
      gap: 1rem;
    }
  }

  &__grid-item {
    background-color: $white;
    border-radius: 0.5rem;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba($black-100, 0.1);
    transition: all 0.2s ease;
    cursor: pointer;
    display: flex;
    flex-direction: column;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba($black-100, 0.15);
    }

    &--selected {
      border: 2px solid $brown-200;
    }

    input[type='checkbox'] {
      width: 1.5rem;
      height: 1.5rem;
      cursor: pointer;
      appearance: none;
      -webkit-appearance: none;
      border: 2px solid $brown-300;
      border-radius: 0.25rem;
      position: relative;
      transition: all 0.2s ease;

      &:hover {
        border-color: $brown-200;
      }

      &:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba($brown-200, 0.3);
      }

      &:checked {
        background-color: $brown-200;
        border-color: $brown-200;

        &::after {
          content: '✓';
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          color: $white;
          font-size: 1rem;
          font-weight: bold;
          line-height: 1;
        }
      }
    }
  }

  &__preview {
    position: relative;
    width: 100%;
    padding-top: 100%;
    background-color: $brown-600;
    overflow: hidden;
    transition: all 0.3s ease;

    @media (max-width: $media-max-phone) {
      padding-top: 75%;
    }

    ::v-deep(.p-image) {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    ::v-deep(.p-image-preview) {
      img {
        max-width: 90vw;
        max-height: 90vh;
        object-fit: contain;
      }
    }
  }

  &__preview-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
    will-change: transform;
  }

  &__preview:hover {
    .file-manager__preview-image {
      transform: scale(1.05);
    }
  }

  &__preview-loading {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba($brown-600, 0.8);
  }

  &__preview-placeholder {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $brown-300;
    font-size: 2rem;
    background-color: $brown-600;
    transition: all 0.3s ease;
  }

  &__item-info {
    padding: 1rem;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    @media (max-width: $media-max-phone) {
      padding: 0.5rem;
    }
  }

  &__filename {
    display: block;
    font-weight: 600;
    color: $brown-100;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__item-meta {
    display: flex;
    gap: 1rem;
    font-size: 0.875rem;
    color: $brown-300;
  }

  &__wish {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem;
    background-color: $brown-600;
    border-radius: 0.25rem;
    margin-top: auto;

    &-text {
      color: $brown-100;
      font-size: 0.875rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__item-actions {
    display: flex;
    gap: 0.5rem;
    padding: 1rem;

    @media (max-width: $media-max-phone) {
      padding: 0.5rem;
    }
  }

  &__list {
    flex: 1;
    overflow: auto;

    @media (max-width: $media-max-phone) {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }
  }

  &__table {
    width: 100%;
    border-collapse: collapse;

    @media (max-width: $media-max-phone) {
      min-width: 600px;
    }
  }

  &__table-header {
    padding: 1rem;
    text-align: left;
    background-color: $white-200;
    color: $brown-100;
    font-weight: 600;

    input[type='checkbox'] {
      width: 1.5rem;
      height: 1.5rem;
      cursor: pointer;
      appearance: none;
      -webkit-appearance: none;
      border: 2px solid $brown-300;
      border-radius: 0.25rem;
      position: relative;
      transition: all 0.2s ease;

      &:checked {
        background-color: $brown-200;
        border-color: $brown-200;

        &::after {
          content: '✓';
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          color: $white;
          font-size: 1rem;
          font-weight: bold;
          line-height: 1;
        }
      }

      &:hover {
        border-color: $brown-200;
      }

      &:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba($brown-200, 0.3);
      }
    }
  }

  &__table-row {
    border-bottom: 1px solid $brown-300;

    &:hover {
      background-color: $white-200;
    }

    &--selected {
      background-color: $brown-600;
    }
  }

  &__table-cell {
    padding: 1rem;
    vertical-align: middle;

    @media (max-width: $media-max-phone) {
      padding: 0.5rem;
    }

    input[type='checkbox'] {
      width: 1.5rem;
      height: 1.5rem;
      cursor: pointer;
      appearance: none;
      -webkit-appearance: none;
      border: 2px solid $brown-300;
      border-radius: 0.25rem;
      position: relative;
      transition: all 0.2s ease;

      &:checked {
        background-color: $brown-200;
        border-color: $brown-200;

        &::after {
          content: '✓';
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          color: $white;
          font-size: 1rem;
          font-weight: bold;
          line-height: 1;
        }
      }

      &:hover {
        border-color: $brown-200;
      }

      &:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba($brown-200, 0.3);
      }
    }
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem;
    color: $brown-300;
    text-align: center;

    &-icon {
      font-size: 4rem;
      margin-bottom: 1rem;
    }

    &-text {
      font-size: 1.5rem;
      color: $brown-100;
      margin-bottom: 0.5rem;
    }

    &-subtext {
      font-size: 1rem;
      color: $brown-300;
    }
  }

  &__pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem;
    background-color: $white-200;
    border-radius: 0.5rem;

    @media (max-width: $media-max-phone) {
      flex-direction: column;
      gap: 1rem;
    }
  }

  &__pagination-controls {
    display: flex;
    align-items: center;
    gap: 1rem;

    @media (max-width: $media-max-phone) {
      width: 100%;
      justify-content: center;
    }
  }

  &__pagination-button {
    background: transparent;
    border: 1px solid $brown-300;
    color: $brown-100;
    padding: 0.5rem;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 3rem;
    min-height: 3rem;
    display: flex;
    align-items: center;
    justify-content: center;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      background-color: $brown-600;
      color: $brown-300;
      transform: none;
      box-shadow: none;
    }

    &:hover:not(:disabled) {
      background-color: $brown-400;
      color: $white;
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba($black-100, 0.1);
    }
  }

  &__pagination-info {
    color: $brown-100;
  }

  &__page-size {
    display: flex;
    align-items: center;

    @media (max-width: $media-max-phone) {
      width: 100%;
      justify-content: center;
    }
  }

  &__page-size-select {
    padding: 0.5rem;
    border: 1px solid $brown-300;
    border-radius: 0.25rem;
    background-color: $white;
    color: $brown-100;
    cursor: pointer;

    &:focus {
      outline: none;
      border-color: $brown-200;
    }
  }

  &__icon {
    display: inline-block;
    width: 1.8rem;
    height: 1.8rem;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;

    &--grid {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='currentColor'%3E%3Cpath d='M3 3h8v8H3V3zm0 10h8v8H3v-8zm10 0h8v8h-8v-8zm0-10h8v8h-8V3z'/%3E%3C/svg%3E");
    }

    &--list {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='currentColor'%3E%3Cpath d='M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z'/%3E%3C/svg%3E");
    }

    &--download {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23ffffff'%3E%3Cpath d='M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z'/%3E%3C/svg%3E");
    }

    &--delete {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23ffffff'%3E%3Cpath d='M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z'/%3E%3C/svg%3E");
    }

    &--upload {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='currentColor'%3E%3Cpath d='M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z'/%3E%3C/svg%3E");
    }

    &--share {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='currentColor'%3E%3Cpath d='M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.39 2.92 3 2.92s3-1.31 3-2.92-1.39-2.92-3-2.92z'/%3E%3C/svg%3E");
    }

    &--heart {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='currentColor'%3E%3Cpath d='M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z'/%3E%3C/svg%3E");
    }

    &--empty {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='currentColor'%3E%3Cpath d='M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z'/%3E%3C/svg%3E");
    }

    &--prev {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='currentColor'%3E%3Cpath d='M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z'/%3E%3C/svg%3E");
    }

    &--next {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='currentColor'%3E%3Cpath d='M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z'/%3E%3C/svg%3E");
    }

    &--close {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23brown-100'%3E%3Cpath d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z'/%3E%3C/svg%3E");
      width: 2rem;
      height: 2rem;
    }
  }

  &__selected-info {
    position: fixed;
    top: 0;
    right: 0;
    width: 300px;
    height: 100vh;
    background-color: $white;
    box-shadow: -2px 0 8px rgba($black-100, 0.1);
    z-index: 1000;
    animation: slide-in-right 0.3s ease-out;
    overflow-y: auto;
  }

  &__selected-info-content {
    display: flex;
    flex-direction: column;
    padding: 1.5rem;
    gap: 1.5rem;
    height: 100%;
  }

  &__selected-info-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
  }

  &__selected-info-title {
    font-size: 1.4rem;
    color: $brown-100;
    margin: 0;
    word-break: break-all;
    flex: 1;
  }

  &__selected-files-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    overflow-y: auto;
    flex: 1;
  }

  &__selected-file-item {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.75rem;
    background-color: $white-200;
    border-radius: 0.5rem;
    transition: all 0.2s ease;

    &:hover {
      background-color: $brown-600;
    }
  }

  &__selected-file-preview {
    width: 50px;
    height: 50px;
    flex-shrink: 0;
    border-radius: 0.25rem;
    overflow: hidden;
    background-color: $brown-600;
    position: relative;

    .file-manager__preview-loading {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: rgba($brown-600, 0.8);
    }

    .file-manager__spinner {
      width: 2rem;
      height: 2rem;
      border: 2px solid $brown-300;
      border-top-color: transparent;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    ::v-deep(.p-image) {
      width: 100%;
      height: 100%;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
  }

  &__selected-file-info {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    flex: 1;
    min-width: 0;
  }

  &__selected-file-name {
    font-size: 1.3rem;
    color: $brown-100;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__selected-file-size {
    font-size: 1.2rem;
    color: $brown-300;
  }

  &__selected-file-remove {
    background: transparent;
    border: none;
    color: $brown-300;
    cursor: pointer;
    padding: 0.5rem;
    transition: color 0.2s ease;
    flex-shrink: 0;

    &:hover {
      color: $brown-100;
    }
  }

  &__selected-info-actions {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-top: auto;
    padding-top: 1rem;
    border-top: 1px solid $brown-300;
  }

  &__selected-info-close {
    background: transparent;
    border: none;
    color: $brown-100;
    cursor: pointer;
    padding: 0.5rem;
    transition: color 0.2s ease;
    flex-shrink: 0;
    width: 3rem;
    height: 3rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.25rem;

    &:hover {
      color: $brown-100;
      background-color: $brown-600;
    }

    .file-manager__icon {
      width: 2rem;
      height: 2rem;
      color: $brown-100;
    }
  }

  &__toggle-button {
    position: fixed;
    top: 50%;
    right: 0;
    transform: translateY(-50%) rotate(-90deg);
    transform-origin: right center;
    background-color: $brown-200;
    color: $white;
    border: none;
    padding: 1rem 2rem;
    border-radius: 0.5rem 0.5rem 0 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 1rem;
    box-shadow: -2px 0 8px rgba($black-100, 0.1);
    transition: all 0.2s ease;
    z-index: 999;
    margin-right: 2rem;

    &:hover {
      background-color: $brown-100;
      padding-right: 3rem;
    }

    .file-manager__icon {
      width: 2rem;
      height: 2rem;
      transform: rotate(90deg);
      color: $white;
      filter: brightness(0) invert(1);
    }
  }

  &__toggle-button-text {
    font-size: 1.4rem;
    white-space: nowrap;
    color: $white;
  }

  &__back-button {
    width: auto;
    height: 3rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid $brown-300;
    color: $brown-100;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.2s ease;
    padding: 1rem;
    gap: 1rem;

    &:hover {
      background-color: $brown-400;
    }

    &--active {
      background-color: $brown-300;
      color: $white;
    }

    i {
      font-size: 1.4rem;
    }

    span {
      font-size: 1.4rem;
    }
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes slide-in {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.image-enter-active,
.image-leave-active {
  transition: all 0.3s ease;
}

.image-enter-from,
.image-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

@keyframes slide-up {
  from {
    transform: translate(-50%, 100%);
    opacity: 0;
  }
  to {
    transform: translate(-50%, 0);
    opacity: 1;
  }
}

@keyframes slide-in-right {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}
</style>
